import React from 'react';
import { motion } from 'framer-motion';
import BlueHeaderSection from '../BlueHeader';

// Asset Imports
import salesforceImg1 from "../../asset/images/web1.png";
import salesforceImg2 from "../../asset/images/web3.png";
import salesforceImg3 from "../../asset/images/mobile-dev1.png";
import salesforceImg4 from "../../asset/images/mobile-dev3.png";

// Shared Animation Constant
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.4, ease: 'easeOut' } 
  }
};

const services = [
  {
    id: "sf-service-1",
    title: 'Salesforce AppExchange',
    text: 'Leverage powerful third-party applications and custom solutions from the Salesforce AppExchange to extend your CRM capabilities and enhance productivity.',
  },
  {
    id: "sf-service-2",
    title: 'Salesforce Consulting Services',
    text: 'Get strategic consulting to optimize your Salesforce org, streamline processes, and align CRM strategy with business objectives.',
  },
  {
    id: "sf-service-3",
    title: 'Salesforce Customization',
    text: 'Tailor Salesforce with custom fields, workflows, objects, and UI so it fits your exact processes instead of forcing you to adapt to the tool.',
  },
  {
    id: "sf-service-4",
    title: 'Salesforce Implementation',
    text: 'End-to-end implementation including planning, configuration, data migration, user training, and go-live support for smooth adoption.',
  },
  {
    id: "sf-service-5",
    title: 'Salesforce Integration',
    text: 'Connect Salesforce with your existing systems, third-party apps, and databases to create a unified, real-time data ecosystem.',
  },
  {
    id: "sf-service-6",
    title: 'Salesforce Managed Services',
    text: 'Ongoing support, maintenance, and optimization with regular updates, performance monitoring, and continuous improvement.',
  }
];

const features = [
  {
    id: "sf-feat-1",
    title: "Certified Salesforce Expertise",
    description: "Work with certified Salesforce professionals experienced across Sales Cloud, Service Cloud, Experience Cloud, and custom apps.",
    image: salesforceImg1
  },
  {
    id: "sf-feat-2",
    title: "Proven Implementation Success",
    description: "We follow a tested implementation framework that reduces risk, improves adoption, and delivers measurable outcomes.",
    image: salesforceImg2
  },
  {
    id: "sf-feat-3",
    title: "Scalable CRM Solutions",
    description: "Our architectures and configurations are designed to grow with your teams, data volume, and processes.",
    image: salesforceImg3
  },
  {
    id: "sf-feat-4",
    title: "24/7 Support & Maintenance",
    description: "Round-the-clock support options to keep your Salesforce org secure, up-to-date, and performing reliably.",
    image: salesforceImg4
  },
];

const SalesforceServices = () => {
  return (
    <div className="overflow-hidden">
      <BlueHeaderSection heading="SALESFORCE DEVELOPMENT" url="Salesforce Development" showServices={true} />

      {/* Content Section */}
      <section className="py-12 md:py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          
          {/* Main Heading */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Empowering Your Business with Salesforce CRM Solutions
            </h2>
            <div className="w-24 h-1 bg-[#f7941d] mx-auto"></div>
          </div>

          {/* Intro */}
          <div className="mb-16 text-center">
            <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto">
              At **BytesFarms Technologies**, we unlock the full potential of Salesforce to transform customer relationships, streamline operations, and drive growth.
            </p>
          </div>

          {/* Services List - Animated exactly like DevOps */}
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((item) => (
              <motion.div
                key={item.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="p-6 rounded-2xl bg-white shadow-md border-l-4 border-[#f7941d] hover:shadow-lg transition-all duration-300"
              >
                <h4 className="text-xl font-semibold text-[#f7941d] mb-3">{item.title}</h4>
                <p className="text-gray-800 leading-relaxed tracking-tight">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section - Fixed Duplication with Unique Keys */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Why choose BytesFarms for Salesforce Development?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {features.map((feature) => (
              <motion.div
                key={feature.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="group relative overflow-hidden rounded-2xl shadow-2xl h-[220px] md:h-[250px]"
              >
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-opacity duration-300"></div>
                
                <div className="relative z-10 p-6 md:p-8 h-full flex flex-col justify-center text-center">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-200 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SalesforceServices;